import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col } from 'antd';
import StaggeredContainer from '../common/StaggeredContainer';
import {
  DownOutlined,
  MinusCircleOutlined,
  ShoppingCartOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { Dropdown, Button } from 'antd';
import {
  getEnabledProducts,
  createCart,
  addItemsToCart,
  removeItemsFromCart,
} from '../../utils/shopify';
import { getStoreName } from '../../actions/shopifyActions';

import './Merch.scss';

const Merch = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [cart, setCart] = useState(null);
  const { data: currentMusician } = useSelector(
    state => state.home.currentMusician
  );
  const { data: storeName } = useSelector(state => state.shopify.storeName);
  const dispatch = useDispatch();

  console.log('DEBUG (shopify museit API): storeName:', storeName);

  const defaultSize = useMemo(() => {
    return selectedProduct ? selectedProduct.variants[0].title : null;
  }, [selectedProduct]);

  const defaultQuantity = 1;

  useEffect(() => {
    dispatch(getStoreName());
  }, [dispatch]);

  useEffect(() => {
    const getProducts = async () => {
      const p = await getEnabledProducts(currentMusician.owner);
      setProducts(() => p);
    };

    const setupCart = async () => {
      try {
        const cart = await createCart();
        setCart(cart);
      } catch (error) {
        setCart(null);
        alert(
          'Failed to initialize shopping cart. Please refresh the page and try again.'
        );
      }
    };

    if (currentMusician && currentMusician.id && !cart) {
      getProducts();
      setupCart();
    }
  }, [currentMusician.id, cart]);

  useEffect(() => {
    switch (currentStep) {
      case 1:
        setSelectedProduct(null);
        setSelectedSize(null);
        setSelectedQuantity(null);
        break;
      default:
        break;
    }
  }, [currentStep]);

  const handleSizeSelect = selection => {
    setSelectedSize(selection.key);
  };

  const handleQuantitySelect = selection => {
    setSelectedQuantity(parseInt(selection.key));
  };

  const handleSelectionsSubmit = useCallback(async () => {
    if (!cart) {
      console.error('Cart is not available');
      alert(
        'Shopping cart is not ready. Please refresh the page and try again.'
      );
      return;
    }

    const size = selectedSize || defaultSize;
    const variant = selectedProduct.variants.find(
      variant => variant.title === size
    );

    if (!variant) {
      console.error('Variant not found for size:', size);
      alert('Selected size is not available.');
      return;
    }

    try {
      const lines = [
        {
          merchandiseId: variant.id,
          quantity: selectedQuantity || defaultQuantity,
        },
      ];
      const newCart = await addItemsToCart(cart.id, lines);
      setCart(newCart);
      setCurrentStep(3);
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  }, [cart, selectedSize, defaultSize, selectedProduct, selectedQuantity]);

  const handleCheckoutSubmit = useCallback(() => {
    if (cart) {
      window.open(cart.checkoutUrl, '_blank');
      setCurrentStep(4);
    }
  }, [cart]);

  const handleCartClick = () => {
    setCurrentStep(3);
  };

  const handleBackClick = () => {
    setCurrentStep(1);
  };

  const handleRemoveFromCartClick = useCallback(
    async index => {
      const lineIds = [cart.lines.edges[index].node.id];
      const newCart = await removeItemsFromCart(cart.id, lineIds);
      setCart(newCart);
    },
    [cart]
  );

  const sizeItems = selectedProduct
    ? selectedProduct.variants.map(variant => ({
        key: variant.title,
        label: variant.title,
      }))
    : [];
  const quantityItems = [1, 2, 3, 4].map(quantity => ({
    key: quantity,
    label: quantity,
  }));

  const productElms = products
    .map((product, index) => {
      if (currentStep !== 1) return null;
      return (
        <div
          className="products__product"
          onClick={() => {
            setSelectedProduct(product);
            setCurrentStep(2);
          }}
          key={`2-${index}`}
        >
          {product.images?.length > 0 &&
          (product.images[0].src || product.images[0].url) ? (
            <img
              src={product.images[0].src || product.images[0].url}
              className="product__img"
              alt={product.title}
            />
          ) : (
            <div className="product__img product__img--placeholder">
              No Image
            </div>
          )}
          <p className="product__title">{product.title}</p>
          <p className="product__price">
            $
            {product.variants?.[0]?.price?.amount ||
              product.variants?.[0]?.price ||
              '25.00'}
          </p>
        </div>
      );
    })
    .filter(Boolean);

  const cartElms =
    !cart || !cart.lines
      ? null
      : cart.lines.edges.map((edge, index) => {
          const lineItem = edge.node;
          return (
            <div className="merch__product-in-cart" key={`2-${index}`}>
              <div className="product__remove">
                <MinusCircleOutlined
                  style={{
                    fontWeight: 'bold',
                    color: 'red',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    handleRemoveFromCartClick(index);
                  }}
                />
              </div>
              <div className="product__quantity">{`${lineItem.quantity} x `}</div>
              {lineItem.merchandise.image &&
              (lineItem.merchandise.image.src ||
                lineItem.merchandise.image.url) ? (
                <img
                  src={
                    lineItem.merchandise.image.src ||
                    lineItem.merchandise.image.url
                  }
                  className="product__img"
                  alt={lineItem.merchandise.title}
                />
              ) : (
                <div className="product__img product__img--placeholder">
                  No Image
                </div>
              )}
              <div className="product__price-size">
                <div className="price">
                  ${lineItem.merchandise.price.amount}
                </div>
                <div className="size">{lineItem.merchandise.title}</div>
              </div>
            </div>
          );
        });

  if (products.length === 0) return null;

  return (
    <>
      {/* Creates the general bounds */}
      <Col
        className="merch-wrapper"
        xs={24}
        sm={24}
        md={24}
        lg={24}
        xl={24}
        xxl={24}
        justify="center"
        align="middle"
      >
        {/* Centers and defines additional bounds */}
        <div className="merch">
          {/* We need because multiple QueueAnims due to products grid */}
          <section className="step-1">
            <StaggeredContainer
              type="bottom"
              delay={1600}
              staggerDelay={100}
              duration={400}
              appear={false}
              enabled={currentStep === 1}
            >
              {currentStep === 1 && (
                <h4 key="1" className="merch__title">
                  Merch
                </h4>
              )}
            </StaggeredContainer>
            <StaggeredContainer
              className={'step-1__products'}
              type="bottom"
              delay={1600}
              staggerDelay={100}
              duration={400}
              appear={false}
              enabled={currentStep === 1}
            >
              {productElms}
            </StaggeredContainer>
          </section>

          {/* Positioned absolutely because we want to overlay on top of it freely */}
          <StaggeredContainer
            className={'beyond-step-1__background'}
            type="bottom"
            delay={1600}
            staggerDelay={100}
            duration={400}
            appear={false}
            enabled={currentStep > 1}
          >
            {currentStep > 1 && (
              <div className="merch__background" key="0"></div>
            )}
          </StaggeredContainer>

          {/* We use to keep things organized and consistent */}
          <section className="step-2">
            <StaggeredContainer
              type="bottom"
              delay={1600}
              staggerDelay={100}
              duration={400}
              className={'step-2__selections'}
              enabled={currentStep === 2}
            >
              {currentStep === 2 && [
                <h2 key="1" className="merch__title">
                  {selectedProduct.title}
                </h2>,
                <div key="2" className="selections__dropdown">
                  <div className="dropdown__label">Size</div>
                  <Dropdown
                    menu={{
                      items: sizeItems,
                      selectable: true,
                      defaultSelectedKeys: [defaultSize],
                      onSelect: handleSizeSelect,
                    }}
                    overlayClassName="dropdown__overlay"
                  >
                    <Button className="dropdown__btn">
                      {selectedSize || defaultSize}
                      <div className="btn__icon">
                        <DownOutlined />
                      </div>
                    </Button>
                  </Dropdown>
                </div>,
                <div key="3" className="selections__dropdown">
                  <div className="dropdown__label">Quantity</div>
                  <Dropdown
                    menu={{
                      items: quantityItems,
                      selectable: true,
                      defaultSelectedKeys: [defaultQuantity],
                      onSelect: handleQuantitySelect,
                    }}
                    overlayClassName="dropdown__overlay"
                  >
                    <Button className="dropdown__btn">
                      {selectedQuantity || defaultQuantity}
                      <div className="btn__icon">
                        <DownOutlined />
                      </div>
                    </Button>
                  </Dropdown>
                </div>,
                <hr key="4" className="merch__line" />,

                <div key="5" className="merch__product-in-cart">
                  <div className="product__quantity">{`${
                    selectedQuantity || defaultQuantity
                  } x `}</div>
                  {selectedProduct.images?.length > 0 &&
                  (selectedProduct.images[0].src ||
                    selectedProduct.images[0].url) ? (
                    <img
                      src={
                        selectedProduct.images[0].src ||
                        selectedProduct.images[0].url
                      }
                      className="product__img"
                      alt={selectedProduct.title}
                    />
                  ) : (
                    <div className="product__img product__img--placeholder">
                      No Image
                    </div>
                  )}
                  <div className="product__price-size">
                    <div className="price">
                      $
                      {selectedProduct.variants[0].price.amount ||
                        selectedProduct.variants[0].price}
                    </div>
                    <div className="size">{selectedSize || defaultSize}</div>
                  </div>
                </div>,
                <Button
                  key="6"
                  justify="center"
                  className={'btn-custom-submit merch__submit-btn'}
                  type="primary"
                  htmlType="button"
                  onClick={handleSelectionsSubmit}
                >
                  Add to Cart
                </Button>,
              ]}
            </StaggeredContainer>
          </section>
          <section className="step-3">
            <StaggeredContainer
              type="bottom"
              delay={1600}
              staggerDelay={100}
              duration={400}
              className="step-3__cart"
              enabled={currentStep === 3}
            >
              {currentStep === 3 && [
                <h2 key="1" className="merch__title">
                  Merch Cart
                </h2>,
                ...(cartElms || []),
                <hr key="2" className="merch__line" />,
                <Button
                  key="3"
                  justify="center"
                  className={'btn-custom-submit merch__submit-btn'}
                  type="primary"
                  htmlType="button"
                  onClick={handleCheckoutSubmit}
                >
                  Proceed to Checkout
                </Button>,
              ]}
            </StaggeredContainer>
          </section>
          <section className="step-4">
            <StaggeredContainer
              type="bottom"
              delay={1600}
              staggerDelay={100}
              duration={400}
              className="step-4__checkout"
              enabled={currentStep === 4}
            >
              {currentStep === 4 && [
                <div key="1" className="next-tab">
                  <p className="main-text">Checkout in next tab.</p>
                  <p className="subtext">
                    Don't see a link? Click{' '}
                    <a href={cart.checkoutUrl} target="_blank">
                      here
                    </a>
                    .
                  </p>
                </div>,
              ]}
            </StaggeredContainer>
          </section>
          <StaggeredContainer
            type="top"
            delay={1600}
            staggerDelay={100}
            duration={400}
            enabled={currentStep !== 1}
          >
            {currentStep !== 1 && (
              <div
                className="merch__floating-btn back"
                onClick={handleBackClick}
                key="1"
              >
                <LeftOutlined className="floating-btn__back-icon" />
                <div className="floating-btn__text">Shop</div>
              </div>
            )}
          </StaggeredContainer>
          <StaggeredContainer
            type="top"
            delay={1600}
            staggerDelay={100}
            duration={400}
            enabled={currentStep !== 3}
          >
            {currentStep !== 3 && (
              <div
                className="merch__floating-btn cart"
                onClick={handleCartClick}
                key="2"
              >
                <div className="floating-btn__cart-icon-wrapper">
                  <ShoppingCartOutlined className="floating-btn__cart-icon" />
                  {cart && cart.lines && cart.lines.edges.length > 0 && (
                    <div className="floating-btn__cart-filled-indicator" />
                  )}
                </div>
              </div>
            )}
          </StaggeredContainer>
        </div>
      </Col>
    </>
  );
};

export default Merch;
